import { EstateType, Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import { IEstimations } from '@mfe-types/context';
import { TRACKING_PORTAL_ACRONYMS } from '@mfe-types/portal';
import {
    EAvCategory,
    EAvDistributionTypeGlobal,
    EAvEstateTypeGlobal,
    EAvInitiative,
} from '@mfe-types/tracking';

const METHOD_NEW = 'new';
const METHOD_UPDATE = 'update';
const AV_MEDIUM_PARAM_KEY = 'm';

enum FUNNEL_TYPE {
    First = 'first', // when an account is created in the funnel
    New = 'new', // all the estimation journey from step 1
    Update = 'update', // update of estimation and not all the steps are fullfilled
}

export const getUserTrackingData = (formValues: Record<string, unknown>, portal: Portal) => {
    const user: { sel?: { qval: string }; project?: string } = {};

    if (
        formValues.isAPrivatePerson &&
        (formValues.ownerObjective || formValues.nonOwnerObjective)
    ) {
        user.project = formValues.isPropertyOwner
            ? `Owner_${formValues.ownerObjective}`
            : `NonOwner_${formValues.nonOwnerObjective}`;
    }

    if (formValues.job) {
        user.project = `Pro_${formValues.job}`;
    }

    if (portal === Portal.ImmoWelt) {
        user.sel = { qval: '1' };
    }
    return Object.keys(user).length ? user : undefined;
};

export const composeGenericDatalayer = ({
    portal,
    locale,
}: {
    portal: Portal;
    locale: TLocale;
}) => {
    const urlParams = new URLSearchParams(window.location.search);

    return {
        av_medium: urlParams.get(AV_MEDIUM_PARAM_KEY),
        av_environment:
            process.env.NX_TASK_TARGET_CONFIGURATION === 'production'
                ? 'prod'
                : process.env.NX_TASK_TARGET_CONFIGURATION === 'preview'
                ? 'preprod'
                : 'staging',
        av_language: locale,
        av_site: TRACKING_PORTAL_ACRONYMS[portal],
        av_platform: 'web',
    };
};

const composeGenericPageViewPayload = ({
    pageTitle,
    category,
    formValues,
    estimation,
    initiative,
    portal,
}: {
    pageTitle: string;
    category: string;
    formValues: Record<string, any>;
    estimation?: IEstimations;
    initiative: string;
    portal: Portal;
}) => {
    const userProject = getUserTrackingData(formValues, portal);
    return {
        name: 'page_view',
        data: {
            ...(userProject && { av_user: userProject }),
            ...(estimation?.id && {
                av_estimation_value: estimation.valuation.sellPrice.toString(),
                av_estimation_id: estimation.id,
            }),
            av_initiative: initiative,
            av_pagetitle: pageTitle,
            av_url_path: window.location.pathname,
            av_category: category,
        },
    };
};

export const composePageViewPayload = ({
    stepIndex,
    pageTitle,
    formValues,
    estimation,
    portal,
}: {
    stepIndex: number;
    pageTitle: string;
    formValues: Record<string, any>;
    estimation?: IEstimations;
    portal: Portal;
}) => {
    /**
     * As there is currently no authentication or estimation update,
     * the funnel type sent in the page view event is always 'first'.
     */
    const funnelType = FUNNEL_TYPE.New;
    const payload = composeGenericPageViewPayload({
        pageTitle: `${pageTitle}_${funnelType}`,
        category: EAvCategory.EstimationFunnel,
        formValues,
        estimation,
        initiative: EAvInitiative.EstimationFunnel,
        portal,
    });

    return {
        ...payload,
        data: {
            ...payload.data,
            ...(stepIndex > 1 && {
                av_estate_type_global: EAvEstateTypeGlobal[formValues.estateType as EstateType],
            }),
            ...(formValues.estateSubType && { av_estate_subtype: formValues.estateSubType }),
            ...(formValues.postalCode && { av_zip_code: formValues.postalCode }),
            ...(formValues.cityName && { av_city: formValues.cityName }),
        },
    };
};

export const composeMPUPageViewPayload = ({
    pageTitle,
    formValues,
    estimation,
    category,
    portal,
}: {
    pageTitle: string;
    formValues: Record<string, any>;
    estimation?: IEstimations;
    category: string;
    portal: Portal;
}) => {
    return composeGenericPageViewPayload({
        pageTitle,
        category,
        formValues,
        estimation,
        initiative: EAvInitiative.MonthlyPriceUpdate,
        portal,
    });
};

const composeGenericInteractionDatalayer = ({
    category,
    formValues,
    initiative,
    estimation,
    portal,
}: {
    category: EAvCategory;
    formValues: Record<string, any>;
    estimation?: IEstimations;
    initiative: string;
    portal: Portal;
}) => {
    const userProject = getUserTrackingData(formValues, portal);
    return {
        ...(userProject && { av_user: userProject }),
        ...(estimation?.id && {
            av_estimation_value: estimation.valuation.sellPrice.toString(),
            av_estimation_id: estimation.id,
        }),
        av_initiative: initiative,
        av_category: category,
        av_estate_type_global: EAvEstateTypeGlobal[formValues.estateType as EstateType],
        av_estate_subtype: formValues.estateSubType,
        av_estimation_nb: estimation?.countEstimations,
        method: estimation
            ? estimation.isAnUpdate
                ? METHOD_UPDATE
                : METHOD_NEW
            : initiative === EAvInitiative.EstimationFunnel
            ? METHOD_NEW
            : METHOD_UPDATE,
    };
};

export const composeInteractionPayload = ({
    eventName,
    formValues,
    estimation,
    portal,
}: {
    eventName: string;
    formValues: Record<string, any>;
    estimation?: IEstimations;
    portal: Portal;
}) => {
    const datalayer = composeGenericInteractionDatalayer({
        category: EAvCategory.EstimationFunnel,
        formValues,
        estimation,
        initiative: EAvInitiative.EstimationFunnel,
        portal,
    });

    return {
        name: eventName,
        data: {
            ...datalayer,
        },
    };
};

export const composeMPUInteractionPayload = ({
    eventName,
    formValues,
    estimation,
    category,
    rawData,
    portal,
}: {
    eventName: string;
    formValues: Record<string, any>;
    estimation?: IEstimations;
    category: EAvCategory;
    rawData?: Record<string, any>;
    portal: Portal;
}) => {
    const datalayer = composeGenericInteractionDatalayer({
        category,
        formValues,
        estimation,
        initiative: EAvInitiative.MonthlyPriceUpdate,
        portal,
    });

    return {
        name: eventName,
        data: {
            ...datalayer,
            av_distribution_type_global: EAvDistributionTypeGlobal.Buy,
            ...rawData,
        },
    };
};

export const composeMPUMediumParams = (currentParams: string, nextStepName: string): string => {
    const params = new URLSearchParams(currentParams);

    if (nextStepName === 'address') {
        params.set(AV_MEDIUM_PARAM_KEY, 'estimate_new_property_estimate_estimate_step1');
    }

    if (nextStepName === 'loading') {
        params.set(AV_MEDIUM_PARAM_KEY, 'update_estimation_see_new_price_estimate_result');
    }

    const paramsString = params.toString();

    return paramsString ? '?' + paramsString : '';
};

export const removeTrackingParams = (currentParams: string) => {
    const searchParams = new URLSearchParams(currentParams);
    const params = Array.from(searchParams);

    for (const element of params) {
        const [key] = element;
        if (key.startsWith('utm') || key === 'm') {
            searchParams.delete(key);
        }
    }
    const newParams = searchParams.toString();
    return newParams.length ? '?' + newParams : '';
};
